import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { richiestaStream } from "../lib/richiesta_stream"

// VociEditor di components/VociEditor.js e saveVoci/load di pages/ProjectDetail.js (31a5bd1), su
// progetti/passo3/_voci_editor. Come in React le schede si disegnano qui, dalle voci e dai dati che erano le props:
// - `menu` (voci-menu): il menu dei bandi (groupGrantsMenu + menuConScelti), le categorie suggeribili
//   (project.categorie_bandi) e le etichette delle categorie; letto con il progetto e i bandi (load());
// - `dati` (voci-dati): le voci salvate (project.voci), il calcolo dello scenario per voce, lo stato, le categorie del
//   bando dagli atti (guida.categorie_bando) e, per ogni voce, le righe calcolate dal server (contributo, StatoVoce,
//   avvisi; gli avvisi della guida), con la revisione del progetto: dati più vecchi di quelli in pagina si ignorano.
//
// Le voci mostrate sono le voci salvate più le modifiche non ancora salvate (update, add, remove), tenute in ordine:
// ogni modifica toglie `automatica` dalla voce («any change by the consultant makes the item theirs»), «Aggiungi voce»
// ne crea una con id `v_${Date.now()}_${rand5}`. Quando arrivano voci salvate nuove (un salvataggio, «Seleziona», una
// conferma) le modifiche non salvate restano sopra di esse, e il PUT parte sempre da lì: una voce creata nel frattempo
// non si perde. 600 ms dopo l'ultima modifica un solo PUT a urlValue ({ voci }); la risposta (turbo-stream) porta
// `voci-dati`, la traccia e la testata; le modifiche che il PUT ha salvato si tolgono quando arrivano voci salvate
// almeno di quella revisione (X-Revisione). Un errore è il toast; 409 e 422 rileggono menu, voci e testata
// (GET urlValue, load()) e lasciano solo le modifiche successive.
//
// Ciò che React calcolava dai dati in pagina si ricalcola qui a ogni modifica: categoria (con le categorie del bando
// della voce), blocchi del personale e del rapporto, menu del bando, intervento forfettario, «Importo da completare»,
// righe del calcolo (solo se bando e importo sono quelli calcolati, calcOk), risposte alle esclusioni, voce
// automatica, suggerimento (suggestBando) e copertura.
const DA_CLASSIFICARE = "da_classificare"
const ATTESA = 600
// il corpo di una richiesta keepalive ha un limite di 64 KiB: oltre, un PUT normale
const KEEPALIVE_MAX = 60000
const GRUPPI = ["scelti", "scenario", "proposed", "compatible", "notSuitable"]
const uid = () => `v_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
const attributo = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
// `${x}` di un template di JavaScript e {x} di JSX (null e undefined non scrivono nulla)
const jsx = (x) => (x === null || x === undefined || x === false || x === true ? "" : String(x))

// formatEuro di lib/api.js
export function eurc(n) {
  const v = Number(n || 0)
  const [int, dec] = Math.abs(v).toFixed(2).split(".")
  return `${v < 0 && Number(Math.abs(v).toFixed(2)) !== 0 ? "-" : ""}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec} €`
}

function elemento(tag, { classe, testid, testo, dati = {}, attr = {} } = {}, figli = []) {
  const el = document.createElement(tag)
  if (classe !== undefined) el.className = classe
  if (testid) el.dataset.testid = testid
  if (testo !== undefined) el.textContent = testo
  Object.entries(dati).forEach(([k, v]) => { el.dataset[k] = v })
  Object.entries(attr).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== false) el.setAttribute(k, v === true ? "" : v) })
  figli.forEach((f) => el.append(f))
  return el
}

// value={x} di un <select> di React: l'opzione con quel valore, altrimenti la prima non disattivata
function seleziona(sel, valore) {
  const opz = [...sel.options]
  const i = opz.findIndex((o) => o.value === valore)
  const indice = i >= 0 ? i : opz.findIndex((o) => !o.disabled)
  if (sel.selectedIndex !== indice) sel.selectedIndex = indice
}

function imposta(el, valore) {
  if (el && el.value !== valore) el.value = valore
}

export default class extends Controller {
  static targets = ["vuoto", "elenco", "scheda", "menu", "dati", "icona"]
  static values = { url: String }

  initialize() {
    this.modifiche = []
    this.versione = 0
    this.attese = []
    this.revisione = -1
    this.giro = 0
  }

  connect() {
    this.primaDellaVisita = () => this.salvaSubito()
    document.addEventListener("turbo:before-visit", this.primaDellaVisita)
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.primaDellaVisita)
    this.salvaSubito()
  }

  // --- i dati ------------------------------------------------------------------------------------------------------

  menuTargetConnected(el) {
    this.menu = JSON.parse(el.dataset.json)
    this.opzioni = el.querySelector("template[data-opzioni-bando]")
    this.giro += 1
    this.disegna()
  }

  datiTargetConnected(el) {
    const d = JSON.parse(el.dataset.json)
    if (d.revisione < this.revisione) return
    this.revisione = d.revisione
    const modelli = (nome) => Object.fromEntries([...el.querySelectorAll(`template[data-${nome}]`)].map((t) => [t.dataset[nome], t.innerHTML]))
    this.dati = { ...d, calcolo: modelli("calcolo"), guida: modelli("guida") }
    // le modifiche che un PUT ha salvato sono ora nelle voci salvate
    this.attese = this.attese.filter((a) => {
      if (a.rev > d.revisione) return true
      this.modifiche = this.modifiche.filter((m) => m.v > a.v)
      return false
    })
    this.disegna()
  }

  // le voci salvate con sopra le modifiche non ancora salvate (lo stato `voci` di ProjectDetail.js)
  locali() {
    let voci = (this.dati?.voci || []).map((v) => ({ ...v }))
    this.modifiche.forEach((m) => {
      if (m.tipo === "aggiungi") {
        if (!voci.some((v) => v.id === m.voce.id)) voci.push({ ...m.voce })
      } else if (m.tipo === "togli") {
        voci = voci.filter((v) => v.id !== m.id)
      } else {
        voci = voci.map((v) => {
          if (v.id !== m.id) return v
          const nv = { ...v, ...m.campi }
          delete nv.automatica
          return nv
        })
      }
    })
    return voci
  }

  modifica(m) {
    this.versione += 1
    this.modifiche.push({ ...m, v: this.versione })
    this.disegna()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      this.salva()
    }, ATTESA)
  }

  aggiorna(id, campi) {
    if (id) this.modifica({ tipo: "imposta", id, campi })
  }

  // --- le modifiche (update di VociEditor) ---------------------------------------------------------------------------

  idDi(event) {
    return event.target.closest?.("[data-voce-id]")?.dataset.voceId
  }

  voce(id) {
    return this.locali().find((v) => v.id === id)
  }

  nome(event) {
    this.aggiorna(this.idDi(event), { nome: event.target.value })
  }

  importo(event) {
    const importo = Number(event.target.value.replace(/[^0-9]/g, "")) || 0
    event.target.value = String(importo)
    this.aggiorna(this.idDi(event), { importo })
  }

  categoria(event) {
    const [riga, cat] = event.target.value.includes("|") ? event.target.value.split("|") : [null, event.target.value]
    this.aggiorna(this.idDi(event), { categoria: cat, categoria_bando: riga })
  }

  rapporto(event) {
    this.aggiorna(this.idDi(event), { rapporto: event.target.value || null, requisito: null, continuativo: null })
  }

  risposta(event) {
    this.aggiorna(this.idDi(event), { [event.params.campo]: event.target.value || null })
  }

  bando(event) {
    this.aggiorna(this.idDi(event), { bandoId: event.target.value || null, intervento: null, categoria_bando: null })
  }

  intervento(event) {
    this.aggiorna(this.idDi(event), { intervento: event.target.value ? { codice: event.target.value, quantita: null } : null })
  }

  quantita(event) {
    const id = this.idDi(event)
    const v = this.voce(id)
    this.aggiorna(id, { intervento: { ...(v?.intervento || {}), quantita: Number(event.target.value) || null } })
  }

  // comune-picker:scelto dal picker dell'unità locale (onChange={(c) => update({ ...v, unita_locale: c })})
  unita(event) {
    this.aggiorna(this.idDi(event), { unita_locale: event.detail?.comune || null })
  }

  assegna(event) {
    this.aggiorna(this.idDi(event), { bandoId: event.currentTarget.dataset.bando })
  }

  // RispostaEsclusione: il motivo (almeno 3 caratteri) prima di «Sì, è esclusa» / «No, non lo è»
  motivo(event) {
    const ok = event.target.value.trim().length >= 3
    event.target.closest("[data-voce-esclusione]")?.querySelectorAll("button").forEach((b) => { b.disabled = !ok })
  }

  rispondi(event) {
    const box = event.currentTarget.closest("[data-voce-esclusione]")
    const input = box?.querySelector("input")
    const motivo = (input?.value || "").trim()
    if (motivo.length < 3) return
    const id = this.idDi(event)
    const v = this.voce(id)
    if (!v) return
    input.value = ""
    box.querySelectorAll("button").forEach((b) => { b.disabled = true })
    const esclusa = event.currentTarget.dataset.esclusa === "true"
    this.aggiorna(id, { esclusioni: { ...(v.esclusioni || {}), [box.dataset.voceEsclusione]: { esclusa, motivo } } })
  }

  cambiaRisposta(event) {
    const id = this.idDi(event)
    const regola = event.currentTarget.closest("[data-voce-risposta]")?.dataset.voceRisposta
    const v = this.voce(id)
    if (!v || regola === undefined) return
    const esclusioni = { ...(v.esclusioni || {}) }
    delete esclusioni[regola]
    this.aggiorna(id, { esclusioni })
  }

  aggiungi() {
    this.modifica({ tipo: "aggiungi", voce: { id: uid(), nome: "Nuova voce di spesa", importo: 0, categoria: DA_CLASSIFICARE, bandoId: null } })
  }

  togli(event) {
    const id = this.idDi(event)
    if (id) this.modifica({ tipo: "togli", id })
  }

  // --- il salvataggio (saveVoci) -------------------------------------------------------------------------------------

  async salva() {
    const versione = this.versione
    const r = await richiestaStream(this.urlValue, { method: "PUT", corpo: { voci: this.locali() }, errore: "Salvataggio voci non riuscito" })
    if (!r) return
    if (r.ok) this.attese.push({ v: versione, rev: Number(r.headers.get("X-Revisione")) })
    Turbo.renderStreamMessage(r.testo)
    // back to what is saved (load()): progetto e bandi riletti; restano solo le modifiche fatte dopo questo PUT
    if (r.status === 409 || r.status === 422) {
      this.modifiche = this.modifiche.filter((m) => m.v > versione)
      this.revisione = -1
      const ricarica = await richiestaStream(this.urlValue, { method: "GET", errore: "Progetto non trovato" })
      if (ricarica) Turbo.renderStreamMessage(ricarica.testo)
    }
  }

  // La modifica che aspetta ancora i 600 ms quando si lascia il passo (una visita Turbo, l'editor tolto dalla pagina):
  // in ProjectDetail.js il timer di saveVoci vive nella pagina, che cambiando passo resta montata, e non si annulla
  // mai, quindi il PUT parte comunque. Qui parte subito, con keepalive perché arrivi anche se la pagina se ne va; la
  // risposta riuscita non si rende (la pagina che arriva ha già la sua testata), un errore resta il toast.
  salvaSubito() {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = null
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const corpo = JSON.stringify({ voci: this.locali() })
    fetch(this.urlValue, {
      method: "PUT", credentials: "same-origin", body: corpo, keepalive: new TextEncoder().encode(corpo).length < KEEPALIVE_MAX,
      headers: { Accept: "text/vnd.turbo-stream.html", "Content-Type": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
    }).then(async (r) => {
      if (r.ok || r.redirected) return
      if ((r.headers.get("Content-Type") || "").includes("turbo-stream")) Turbo.renderStreamMessage(await r.text())
      else window.toast?.error?.("Salvataggio voci non riuscito")
    }).catch(() => window.toast?.error?.("Salvataggio voci non riuscito"))
  }

  // --- il disegno ----------------------------------------------------------------------------------------------------

  schede() {
    return [...this.elencoTarget.children].filter((c) => c.dataset.voceId !== undefined)
  }

  disegna() {
    if (!this.menu || !this.dati) return
    const voci = this.locali()
    const ids = new Set(voci.map((v) => v.id))
    const presenti = new Map(this.schede().map((c) => [c.dataset.voceId, c]))
    presenti.forEach((c, id) => { if (!ids.has(id)) c.remove() })
    voci.forEach((v, i) => {
      const c = presenti.get(v.id) || this.nuovaScheda(v.id)
      const posto = this.elencoTarget.children[i]
      if (posto !== c) this.elencoTarget.insertBefore(c, posto || null)
      this.aggiornaScheda(c, v)
    })
    this.vuotoTarget.hidden = voci.length > 0
    this.elencoTarget.hidden = voci.length === 0
  }

  nuovaScheda(id) {
    const t = document.createElement("template")
    t.innerHTML = this.schedaTarget.innerHTML.replaceAll("__VOCE__", attributo(id))
    return t.content.firstElementChild
  }

  icona(nome) {
    return this.iconaTargets.find((t) => t.dataset.nome === nome).content.firstElementChild.cloneNode(true)
  }

  // un bando del menu (findGrant / outsideGrant)
  bandoDelMenu(id, gruppi) {
    if (!id || !gruppi.some((k) => (this.menu.gruppi[k] || []).includes(id))) return null
    return { id, ...this.menu.bandi[id] }
  }

  spesaLabel(key) {
    if (!key) return ""
    if (key === DA_CLASSIFICARE) return "Da classificare"
    const c = this.menu.categorie.find((x) => x.key === (this.menu.scenario_vecchio[key] || key))
    if (c) return c.label
    return this.menu.vecchie[key] || key
  }

  aggiornaScheda(c, v) {
    const q = (p) => c.querySelector(`[data-parte="${p}"]`)
    const d = this.dati
    const assegnato = this.bandoDelMenu(v.bandoId, GRUPPI)
    const fuori = !assegnato && v.bandoId ? this.bandoDelMenu(v.bandoId, ["outside"]) : null
    // contribution computed by the server on the last saved items (same bando as shown)
    const calc = d.calc[v.id]
    const calcOk = !!calc && calc.bando_id === (v.bandoId || null) && Number(calc.importo) === Number(v.importo)
    const righeBando = d.categorie_bando?.[v.bandoId] || []
    const sx = calcOk ? d.stati[v.id] : undefined
    const importo = Number(v.importo)

    imposta(q("nome"), jsx(v.nome))
    imposta(q("importo"), jsx(v.importo))
    this.menuCategoria(q("categoria"), v, righeBando)
    q("personale").hidden = v.categoria !== "personale"
    this.unitaLocale(q("personale"), v.unita_locale)
    q("persona").hidden = !["personale", "consulenze_servizi"].includes(v.categoria)
    seleziona(q("rapporto"), v.rapporto || "")
    q("blocco-requisito").hidden = !(v.rapporto === "dipendente" || v.rapporto === "collaboratore")
    seleziona(q("requisito"), v.requisito || "")
    q("blocco-continuativo").hidden = v.rapporto !== "consulente"
    seleziona(q("continuativo"), v.continuativo || "")
    this.menuBando(q("bando"), v, fuori, calc)
    this.disegnaIntervento(q("intervento"), v, assegnato)
    q("da-completare").hidden = !(!(importo > 0) && v.bandoId)
    this.blocco(q("calcolo"), calcOk ? (d.calcolo[v.id] || "") : "")
    this.blocco(q("guida"), d.guida[v.id] || "")
    q("guida").querySelectorAll("[data-voce-esclusione]").forEach((b) => { b.hidden = !!(v.esclusioni || {})[b.dataset.voceEsclusione] })
    this.risposte(q("risposte"), v)
    q("automatica").hidden = !v.automatica
    this.suggerimento(q("suggerimento"), v)
    this.copertura(q("copertura"), v, assegnato, fuori, calc, sx)
  }

  // un blocco riscritto solo se cambia (lo stato di StatoVoce e del motivo di una risposta resta)
  blocco(el, html) {
    if (el.vociHtml === html) return
    el.innerHTML = html
    el.vociHtml = html
  }

  // contenuto rifatto solo se la sua firma cambia
  rifai(el, firma, costruisci) {
    if (el.vociFirma === firma) return false
    el.vociFirma = firma
    el.replaceChildren(...costruisci())
    return true
  }

  menuCategoria(sel, v, righe) {
    const opzione = (value, testo, title) => elemento("option", { testo, attr: { value, title } })
    const tutte = () => this.menu.categorie.map((c) => opzione(c.key, c.label))
    const firma = JSON.stringify([v.categoria === DA_CLASSIFICARE, this.menu.vecchie[v.categoria] ? v.categoria : null, righe, this.giro])
    this.rifai(sel, firma, () => {
      const opz = []
      if (v.categoria === DA_CLASSIFICARE) opz.push(opzione(DA_CLASSIFICARE, "Da classificare"))
      if (this.menu.vecchie[v.categoria]) opz.push(opzione(v.categoria, `${this.menu.vecchie[v.categoria]} (vecchia categoria)`))
      if (righe.length > 0) {
        const atto = elemento("optgroup", { attr: { label: "Categorie del bando (dall'atto)" } },
          righe.flatMap((c) => (c.categorie || [c.categoria]).map((k) => opzione(`${c.id}|${k}`,
            `${jsx(c.nome)} — ${this.menu.categorie.find((x) => x.key === k)?.label || jsx(k)}`, `${c.punto}: «${c.citazione}»`))))
        opz.push(atto, elemento("optgroup", { attr: { label: "Altre categorie" } }, tutte()))
      } else {
        opz.push(...tutte())
      }
      return opz
    })
    seleziona(sel, righe.some((c) => c.id === v.categoria_bando) ? `${v.categoria_bando}|${v.categoria}` : jsx(v.categoria))
    const da = v.categoria === DA_CLASSIFICARE
    sel.classList.toggle("border-amber-400", da)
    sel.classList.toggle("text-amber-700", da)
    sel.classList.toggle("border-zinc-300", !da)
  }

  unitaLocale(box, c) {
    const firma = JSON.stringify(c || null)
    if (box.vociFirma === firma) return
    box.vociFirma = firma
    const picker = box.querySelector("[data-controller~='comune-picker']")
    if (!picker) return
    picker.querySelector("[data-comune-picker-target='etichetta']").textContent = c ? `${c.comune} (${c.sigla}) · ${c.regione}` : ""
    picker.querySelector("[data-comune-picker-target='scelto']").hidden = !c
    picker.querySelector("[data-comune-picker-target='ricerca']").hidden = !!c
  }

  menuBando(sel, v, fuori, calc) {
    const ignoto = v.bandoId && !this.bandoDelMenu(v.bandoId, GRUPPI) && !fuori
    const extra = ignoto ? (calc?.bando ? `${calc.bando} (${calc.stato === "sospeso" ? "in verifica sugli atti" : "non disponibile"})` : "Bando non più disponibile") : null
    this.rifai(sel, JSON.stringify([v.bandoId || "", fuori?.id || "", extra, this.giro]), () => {
      const gruppi = [...this.opzioni.content.cloneNode(true).children]
      // a closed grant stays selectable only on the item that already has it
      gruppi.forEach((g) => g.querySelectorAll("option[data-chiuso='true']").forEach((o) => { o.disabled = o.value !== v.bandoId }))
      const opz = [elemento("option", { testo: "— Nessuno assegnato —", attr: { value: "" } }), ...gruppi]
      if (fuori) opz.push(elemento("option", { testo: `${fuori.name} (fuori territorio)`, attr: { value: fuori.id } }))
      if (ignoto) opz.push(elemento("option", { testo: extra, attr: { value: v.bandoId } }))
      return opz
    })
    seleziona(sel, v.bandoId || "")
  }

  // flat amounts per intervention: the item says which intervention of the act's table it is
  disegnaIntervento(box, v, assegnato) {
    const interventi = assegnato?.forfettario ? (assegnato.interventi || []) : []
    const codice = v.intervento?.codice || ""
    const iv = interventi.find((x) => x.codice === v.intervento?.codice)
    const id = v.id
    this.rifai(box, JSON.stringify([assegnato?.id || "", interventi.length > 0, codice, !!iv?.per_unita, this.giro]), () => {
      if (interventi.length === 0) return []
      const sel = elemento("select", {
        classe: `rounded-md border px-2.5 py-1.5 text-xs bg-white min-w-[180px] ${codice ? "border-zinc-300" : "border-amber-400 text-amber-700"}`,
        testid: `voce-intervento-${id}`, dati: { action: "change->voci-editor#intervento" },
      }, [elemento("option", { testo: "— Scegli l'intervento del bando —", attr: { value: "" } }),
        ...interventi.map((x) => elemento("option", { testo: `${x.codice} ${x.nome}`, attr: { value: x.codice } }))])
      seleziona(sel, codice)
      const parti = [sel]
      if (iv?.per_unita) {
        parti.push(elemento("span", { classe: "flex items-center gap-1 text-[11px] text-zinc-500" }, [
          "quantità",
          elemento("input", { classe: "w-16 rounded-md border border-zinc-300 px-2 py-1 text-xs", testid: `voce-intervento-quantita-${id}`,
            dati: { action: "input->voci-editor#quantita" }, attr: { type: "number", min: "1", step: "1" } }),
        ]))
      }
      return parti
    })
    const quantita = box.querySelector("input[type='number']")
    if (quantita) imposta(quantita, String(v.intervento?.quantita ?? 1))
  }

  risposte(box, v) {
    const esclusioni = Object.entries(v.esclusioni || {})
    this.rifai(box, JSON.stringify(esclusioni), () => esclusioni.map(([rid, r]) => elemento("p", {
      classe: "text-[11px] text-zinc-500 mt-1 ml-4", testid: `voce-esclusione-risposta-${v.id}`, dati: { voceRisposta: rid },
    }, [`Possibile esclusione: ${r.esclusa ? "sì, è esclusa" : "no, non lo è"} (${jsx(r.motivo)})${r.da ? ` · ${r.da}` : ""} `,
      elemento("button", { classe: "underline", testo: "cambia", dati: { action: "voci-editor#cambiaRisposta" }, attr: { type: "button" } })])))
  }

  // suggestBando: solo un bando le cui condizioni confermate per categoria comprendono la categoria della voce, prima
  // gli scelti, poi scenario, proposti, compatibili; mai «altro», mai una voce già assegnata
  suggerito(v) {
    const cat = v.categoria
    if (v.bandoId || !this.menu.categorie.some((c) => c.key === cat) || cat === "altro") return null
    const copre = (id) => (this.menu.categorie_bandi[id] || []).includes(cat)
    for (const k of ["scelti", "scenario", "proposed", "compatible"]) {
      const id = (this.menu.gruppi[k] || []).find(copre)
      if (id) return { id, ...this.menu.bandi[id] }
    }
    return null
  }

  suggerimento(box, v) {
    const s = this.suggerito(v)
    this.rifai(box, JSON.stringify([s?.id || null, v.categoria, this.giro]), () => (s ? [elemento("div", {
      classe: "mt-2 flex items-center gap-2 text-[11px] text-indigo-700", testid: `voce-suggest-${v.id}`,
    }, [this.icona("lampadina"),
      elemento("span", {}, [`Categoria ${this.spesaLabel(v.categoria)} prevista dalle condizioni del bando `, elemento("strong", { testo: jsx(s.name) }),
        ` (${s.tipo}): da verificare sulla voce, non significa che la spesa sia finanziabile`]),
      elemento("button", { classe: "rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-semibold hover:bg-indigo-100", testo: "Assegna",
        testid: `voce-suggest-apply-${v.id}`, dati: { action: "voci-editor#assegna", bando: s.id }, attr: { type: "button" } })])] : []))
  }

  // la riga finale della voce: sospesa, chiusa, fuori territorio, non più nel catalogo, coperta, assegnata, non coperta
  copertura(box, v, assegnato, fuori, calc, sx) {
    const importo = Number(v.importo)
    const avviso = (classe, testid, testo, icona = "avviso") => elemento("p", { classe, testid }, [this.icona(icona), testo])
    let spec
    if (calc?.stato === "sospeso") {
      // a grant waiting for its verification (titolare, 18/10/2026): the item keeps its grant, nothing to replace
      spec = ["sospesa", jsx(calc.causa), jsx(calc.motivo)]
    } else if (assegnato && assegnato.chiuso) {
      spec = ["chiuso", assegnato.name, assegnato.motivo_chiuso]
    } else if (!assegnato && !fuori && v.bandoId && calc?.stato === "bando_chiuso") {
      // closed or not visible grants are no longer in the menu: name and reason come from the server
      spec = ["chiuso-server", jsx(calc.bando), jsx(calc.motivo)]
    } else if (fuori) {
      spec = ["fuori", fuori.name]
    } else if (v.bandoId && !assegnato) {
      spec = ["catalogo"]
    } else if (assegnato && (calc?.contributo || 0) > 0 && (!sx || sx === "soddisfatti")) {
      spec = ["coperta", assegnato.name, assegnato.tipo, eurc(calc.contributo)]
    } else if (assegnato) {
      // in green only with a contribution: 0 or not computable is not a coverage (titolare, 08/10/2026); without an
      // amount no formula at all («80% di 0 €»): the item says «Importo da completare» above
      spec = importo > 0 ? ["non-coperta", assegnato.name, assegnato.tipo, calc?.motivo ? `${calc.motivo.replace(/\.$/, "")}.` : "contributo 0 o non calcolabile."] : ["niente"]
    } else {
      spec = ["non-coperta-bando", this.spesaLabel(v.categoria)]
    }
    this.rifai(box, JSON.stringify(spec), () => {
      const [tipo, a, b, c] = spec
      const id = v.id
      switch (tipo) {
        case "sospesa": return [avviso("text-[11px] text-amber-800 mt-2 flex items-start gap-1", `voce-sospesa-${id}`, ` ${a}: ${b} La voce resta assegnata: non serve scegliere un altro bando.`)]
        case "chiuso": return [avviso("text-[11px] text-rose-600 mt-2 flex items-start gap-1", `voce-chiuso-${id}`, ` Bando chiuso, ${a}: ${b} Il contributo non è sommato allo scenario né al de minimis: scegli un altro bando.`)]
        case "chiuso-server": {
          const etichetta = /riverifica/.test(b) ? "Bando sospeso" : /verificato|disattivato/.test(b) ? "Bando non disponibile" : "Bando chiuso"
          return [avviso("text-[11px] text-rose-600 mt-2 flex items-start gap-1", `voce-chiuso-${id}`, ` ${etichetta}, ${a}: ${b}`)]
        }
        case "fuori": return [avviso("text-[11px] text-rose-600 mt-2 flex items-center gap-1", `voce-fuori-${id}`, ` ${a} è fuori territorio per la sede del progetto: scegli un altro bando.`, "avviso-semplice")]
        case "catalogo": return [avviso("text-[11px] text-rose-600 mt-2 flex items-center gap-1", undefined, " Il bando assegnato non è più nel catalogo: scegline un altro.", "avviso-semplice")]
        case "coperta": return [elemento("p", { classe: "text-[11px] text-emerald-600 mt-2", testid: `voce-coperta-${id}` },
          ["Coperta da ", elemento("strong", { testo: a }), ` — ${b}, contributo ${c}.`])]
        case "non-coperta": return [elemento("p", { classe: "text-[11px] text-zinc-500 mt-2", testid: `voce-non-coperta-${id}` },
          ["Assegnata a ", elemento("strong", { testo: a }), ` — ${b}: ${c}`])]
        case "non-coperta-bando": return [avviso("text-[11px] text-amber-600 mt-2 flex items-center gap-1", undefined, ` Voce non ancora coperta da un bando (${a}).`, "avviso-semplice")]
        default: return []
      }
    })
  }
}
